import type { ID, ModelTypes, Response, StoreThis } from '@/types'
import type { ToastState } from '@/types/ui'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'

import { handleErrors, removeFromListBatch } from '@/helpers'
import { ref } from 'vue'

const resource = 'models'

export const useModelsStore = defineStore(resource, () => {
  const ui = useUiStore()

  const isToggling = ref(false)
  const isSorting = ref(false)
  const isEnabling = ref(false)
  const isDisabling = ref(false)
  const isDeleting = ref(false)

  async function toggle(this: StoreThis, id: ID, model: ModelTypes = 'block') {
    const options: Partial<ToastState> = {}
    let error = false

    isToggling.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.put(
        `/m/${resource}/${model}/toggle/${id}`
      )

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[toggle]', model, e)

      options.text = 'Что-то пошло не так. Изменить не удалось'
    } finally {
      isToggling.value = false

      if (options.text) ui.showToast(options)
    }
    return error
  }

  async function sort(this: StoreThis, ids: ID[], model: ModelTypes = 'block') {
    const options: Partial<ToastState> = {}
    let error = false
    isSorting.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.put(`/m/${resource}/${model}/sort`, {
        indexes: ids,
      })

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[sort]', model, e)

      options.text = 'Что-то пошло не так. Отсортировать не удалось'
    } finally {
      isSorting.value = false

      if (options.text) ui.showToast(options)
    }

    return error
  }

  async function enable(
    this: StoreThis,
    ids: ID[],
    model: ModelTypes = 'product'
  ) {
    const options: Partial<ToastState> = {}
    let error = false

    isEnabling.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.put(
        `/m/${resource}/${model}/enable`,
        {},
        { params: { id: ids.join(',') } }
      )

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[enable]', model, e)

      options.text = 'Что-то пошло не так. Изменить не удалось'
    } finally {
      isEnabling.value = false

      if (options.text) ui.showToast(options)
    }
    return error
  }

  async function disable(
    this: StoreThis,
    ids: ID[],
    model: ModelTypes = 'product'
  ) {
    const options: Partial<ToastState> = {}
    let error = false

    isDisabling.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.put(
        `/m/${resource}/${model}/disable`,
        {},
        { params: { id: ids.join(',') } }
      )

      if (!success && errors) throw Error(handleErrors(errors))
    } catch (e) {
      error = true
      console.error('[disable]', model, e)

      options.text = 'Что-то пошло не так. Изменить не удалось'
    } finally {
      isDisabling.value = false

      if (options.text) ui.showToast(options)
    }
    return error
  }

  async function remove(
    this: StoreThis,
    ids: ID[],
    items: any[],
    model: ModelTypes = 'product'
  ) {
    const options: Partial<ToastState> = {}
    let error = false
    isDeleting.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.delete(
        `/m/${resource}/${model}/destroy`,
        {
          params: { id: ids.join(',') },
        }
      )

      if (!success && errors) throw Error(handleErrors(errors))
      /**
       * Удаляем из списка, чтобы не запрашивать заново
       * */
      removeFromListBatch(items, ids)
    } catch (e) {
      error = true
      console.error('[remove]', e)

      options.text = 'Что-то пошло не так. Удалить не удалось'
    } finally {
      isDeleting.value = false

      if (options.text) ui.showToast(options)
    }

    return error
  }

  return {
    isToggling,
    isSorting,
    isEnabling,
    isDisabling,
    isDeleting,
    toggle,
    sort,
    enable,
    disable,
    remove,
  }
})
